/** @file Sets or removes an attachment's item handle (a = base, b = toward the tip, c = 2nd grip). */
import { z } from 'zod';
import { attachmentParts } from '@pose2d/format/doc/schemaParts/attachments';
import { MOUNT_REASONS } from '@pose2d/core/mount/mountReasons.data';
import { throwError } from '@pose2d/format/throwError';
import type { Handle } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';

/** VALIDATION at value.b when the grip axis has zero length (a mount scales by |b − a|). */
function assertAxis(handle: Handle): void {
  if (handle.a[0] === handle.b[0] && handle.a[1] === handle.b[1]) {
    throwError('VALIDATION', undefined, { path: 'value.b', hint: 'the handle axis needs two distinct points: a = base, b = toward the tip' });
  }
}

/** True when both points are equal, or both absent. */
const samePoint = (p: readonly number[] | undefined, q: readonly number[] | undefined): boolean => p?.[0] === q?.[0] && p?.[1] === q?.[1];

/** True when `current` has exactly the points of `next` (an absent `c` only equals an absent `c`). */
const sameHandle = (current: Handle | undefined, next: Handle): boolean =>
  current !== undefined && samePoint(current.a, next.a) && samePoint(current.b, next.b) && samePoint(current.c, next.c);

/** attachment.setHandle: replaces the whole handle (no merge, so `c` can be dropped); null removes it unless mounted. */
export const command = defineCommand({
  name: 'attachment.setHandle',
  description: 'Set the item handle of an attachment in image pixels (origin top-left, y down): value = { a: base, b: toward the tip, c?: second grip } replaces the whole handle; null removes it (refused while the attachment is mounted on a socket).',
  input: z.object({ target: z.string().min(1), value: attachmentParts.handle.nullable() }).strict(),
  apply(draft, input, ctx) {
    const att = draft.attachments[ctx.resolve('attachment', input.target)];
    if (!att) return;
    if (input.value === null) {
      if (att.mount) throwError('MOUNT_INVALID', { attachment: att.name, reason: MOUNT_REASONS.noHandle }, { path: 'value', hint: 'unequip it from its socket first' });
      if (att.handle === undefined) return;
      delete att.handle;
    } else {
      assertAxis(input.value);
      if (sameHandle(att.handle, input.value)) return;
      att.handle = input.value;
    }
    draft.meta.updatedAt = ctx.now;
  },
});
